/**
 * Audio Preview Component
 *
 * Displays an audio player with playback controls, waveform visualization,
 * and optional transcript display.
 *
 * @module features/chat/components/preview/AudioPreview
 */

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ref, getDownloadURL, getMetadata } from "firebase/storage";
import { LoaderCircle, Pause, Play, Trash2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchTranscript } from "../../services/transcript-service";
import { isStorageAudioBlock } from "@/lib/storage";
import { useStorageContext } from "@/contexts/use-storage";
import { getFirebaseStorage } from "@/lib/firebase";
import type {
  Base64ContentBlock,
  ContentBlock,
} from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface AudioPreviewProps {
  block: ContentBlock;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
  size?: "sm" | "md" | "lg";
  label?: number;
  variant?: "compact" | "full";
  transcript?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const AudioPreview: React.FC<AudioPreviewProps> = ({
  block,
  removable = false,
  onRemove,
  className,
  label,
  variant = "compact",
  transcript,
}) => {
  const { userId, conversationId } = useStorageContext();
  const { t } = useTranslation("chat");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(block.metadata?.duration || 0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  // Transcript state
  const [transcriptText, setTranscriptText] = useState<string | undefined>(
    transcript
  );
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);

  // Upload state
  const isUploading = isStorageAudioBlock(block) && block.uploading === true;
  const uploadProgress = isStorageAudioBlock(block)
    ? block.uploadProgress
    : undefined;

  // Audio filename for transcript lookup
  const audioFileName = isStorageAudioBlock(block)
    ? block.fileName || block.relative_gcs_path?.split("/").pop()
    : undefined;

  // Authenticated Firebase Storage URL
  const [authenticatedAudioUrl, setAuthenticatedAudioUrl] = useState<
    string | null
  >(null);

  // Update transcript when prop changes
  useEffect(() => {
    if (transcript) {
      setTranscriptText(transcript);
    }
  }, [transcript]);

  // Fetch authenticated URL and metadata (including duration fallback)
  useEffect(() => {
    if (!isStorageAudioBlock(block) || isUploading) {
      setAuthenticatedAudioUrl(null);
      return;
    }

    if (
      !userId ||
      !conversationId ||
      !audioFileName ||
      audioFileName.startsWith("temp-")
    ) {
      setAuthenticatedAudioUrl(null);
      return;
    }

    const fetchAuthenticatedUrlAndMetadata = async () => {
      try {
        const storage = getFirebaseStorage();
        const storagePath = `users/${userId}/projects/${conversationId}/audio/${audioFileName}`;
        const storageRef = ref(storage, storagePath);

        // Fetch URL and metadata in parallel
        const [url, metadata] = await Promise.all([
          getDownloadURL(storageRef),
          getMetadata(storageRef),
        ]);

        setAuthenticatedAudioUrl(url);

        // If duration is not set from block metadata, try to get it from Storage metadata
        if (duration === 0 && metadata.customMetadata?.duration) {
          const storageDuration = parseFloat(metadata.customMetadata.duration);
          if (!isNaN(storageDuration) && storageDuration > 0) {
            setDuration(storageDuration);
          }
        }
      } catch (error) {
        console.error("[AudioPreview] Failed to get authenticated URL:", error);
        setAuthenticatedAudioUrl(null);
      }
    };

    fetchAuthenticatedUrlAndMetadata();
  }, [block, userId, conversationId, audioFileName, isUploading, duration]);

  // Handle transcript toggle with lazy loading
  const handleTranscriptToggle = async () => {
    if (isTranscriptExpanded) {
      setIsTranscriptExpanded(false);
      return;
    }

    if (transcriptText) {
      setIsTranscriptExpanded(true);
      return;
    }

    if (!userId || !conversationId || !audioFileName) {
      return;
    }

    setIsLoadingTranscript(true);
    setTranscriptError(null);

    try {
      const result = await fetchTranscript(userId, conversationId, audioFileName);

      if (result.transcript) {
        setTranscriptText(result.transcript);
        setIsTranscriptExpanded(true);
      } else {
        setTranscriptError(t("multimodal.transcriptNotAvailable"));
      }
    } catch (error) {
      console.error("[AudioPreview] Failed to fetch transcript:", error);
      setTranscriptError(t("multimodal.transcriptFetchError"));
    } finally {
      setIsLoadingTranscript(false);
    }
  };

  // Compute audio URL
  const audioUrl = (() => {
    if (isStorageAudioBlock(block)) {
      const metadataUrl = block.metadata?.downloadUrl as string | undefined;

      if (isUploading) {
        return metadataUrl || undefined;
      }

      if (authenticatedAudioUrl) {
        return authenticatedAudioUrl;
      }

      if (metadataUrl && metadataUrl.startsWith("blob:")) {
        return metadataUrl;
      }

      return undefined;
    }

    const base64Block = block as Base64ContentBlock;
    if (base64Block.mime_type && base64Block.data) {
      return `data:${base64Block.mime_type};base64,${base64Block.data}`;
    }
    return undefined;
  })();

  // Audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl || isUploading) return;

    let animationFrameId: number;

    const updateTimeSmooth = () => {
      if (audio && !audio.paused) {
        setCurrentTime(audio.currentTime);
        if (
          audio.duration &&
          !isNaN(audio.duration) &&
          isFinite(audio.duration) &&
          audio.duration > 0
        ) {
          setDuration((prev: number) =>
            prev === 0 ? audio.duration : prev
          );
        }
        animationFrameId = requestAnimationFrame(updateTimeSmooth);
      }
    };

    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration);
      } else if (block.metadata?.duration && block.metadata.duration > 0) {
        setDuration(block.metadata.duration);
      }
    };

    const handleCanPlayThrough = () => {
      if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
        setDuration(audio.duration);
      } else if (block.metadata?.duration && block.metadata.duration > 0) {
        setDuration(block.metadata.duration);
      }
    };

    const handleError = () => {
      // Audio loading errors are expected during page transitions
    };

    const handleEnded = () => setIsPlaying(false);

    const handlePlay = () => {
      setIsPlaying(true);
      if (
        audio.duration &&
        !isNaN(audio.duration) &&
        isFinite(audio.duration) &&
        audio.duration > 0
      ) {
        setDuration(audio.duration);
      }
      animationFrameId = requestAnimationFrame(updateTimeSmooth);
    };

    const handlePause = () => {
      setIsPlaying(false);
      cancelAnimationFrame(animationFrameId);
      setCurrentTime(audio.currentTime);
    };

    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("durationchange", updateDuration);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    audio.load();

    return () => {
      cancelAnimationFrame(animationFrameId);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("durationchange", updateDuration);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [audioUrl, isUploading, block.metadata?.duration]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio || isUploading || !audioUrl) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((e) => {
        console.error("Failed to play audio:", e);
      });
    }
  };

  const seekToPosition = (clientX: number) => {
    const audio = audioRef.current;
    const progressBar = progressRef.current;
    if (!audio || !progressBar || duration === 0 || isUploading) return;

    const rect = progressBar.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    seekToPosition(e.clientX);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      seekToPosition(moveEvent.clientX);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Full-width variant for input form
  if (variant === "full") {
    return (
      <div
        className={cn(
          "w-full bg-utrecht-light-blue border-b border-utrecht-blue/20 px-4 py-4",
          className
        )}
      >
        {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}

        <div className="flex items-center gap-3">
          {label !== undefined && (
            <span className="flex h-2 w-2 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-primary">
              {label}
            </span>
          )}

          <button
            type="button"
            onClick={togglePlayback}
            disabled={isUploading}
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors",
              isUploading
                ? "bg-muted-foreground/30 text-muted-foreground cursor-not-allowed"
                : "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
            )}
            aria-label={
              isUploading
                ? t("audio.uploading")
                : isPlaying
                  ? t("multimodal.pause")
                  : t("multimodal.play")
            }
          >
            {isUploading ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="ml-0.5 h-4 w-4" />
            )}
          </button>

          <div
            ref={progressRef}
            onMouseDown={isUploading ? undefined : handleMouseDown}
            className={cn(
              "relative flex-1 h-2 bg-muted-foreground/20 rounded-full group",
              isUploading ? "cursor-default" : "cursor-pointer"
            )}
            role="slider"
            aria-label={
              isUploading ? t("audio.uploading") : t("multimodal.seekAudio")
            }
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={isUploading ? (uploadProgress ?? 0) : Math.round(progress)}
          >
            <div
              className={cn(
                "absolute top-0 left-0 h-full rounded-full",
                isUploading
                  ? "bg-utrecht-gold bg-gradient-to-r from-utrecht-gold via-utrecht-gold/70 to-utrecht-gold animate-pulse"
                  : "bg-primary"
              )}
              style={{
                width: `${isUploading ? (uploadProgress ?? 0) : progress}%`,
              }}
            />
            {!isUploading && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            )}
          </div>

          {!isUploading && (
            <span className="text-xs text-muted-foreground whitespace-nowrap font-mono min-w-[70px] text-right tabular-nums">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          )}

          {isUploading && (
            <span className="text-xs text-muted-foreground whitespace-nowrap font-mono min-w-[40px] text-right">
              {uploadProgress ?? 0}%
            </span>
          )}

          {removable && (
            <button
              type="button"
              className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-full p-1.5 transition-colors cursor-pointer"
              onClick={onRemove}
              aria-label={t("multimodal.removeAudio")}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Compact variant for message history
  return (
    <div
      className={cn(
        "min-w-64 bg-primary/10 rounded-xl rounded-br-none px-3 py-2.5",
        className
      )}
    >
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}

      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={togglePlayback}
          disabled={isUploading}
          className={cn(
            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors",
            isUploading
              ? "bg-muted-foreground/30 text-muted-foreground cursor-not-allowed"
              : "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
          )}
          aria-label={
            isUploading
              ? t("audio.uploading")
              : isPlaying
                ? t("multimodal.pause")
                : t("multimodal.play")
          }
        >
          {isUploading ? (
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="ml-0.5 h-3.5 w-3.5" />
          )}
        </button>

        <div className="flex flex-col gap-0.5 flex-1 min-w-0 self-center">
          <div
            ref={progressRef}
            onMouseDown={isUploading ? undefined : handleMouseDown}
            className={cn(
              "relative h-1.5 bg-primary/20 rounded-full group w-full",
              isUploading ? "cursor-default" : "cursor-pointer"
            )}
            role="slider"
            aria-label={
              isUploading ? t("audio.uploading") : t("multimodal.seekAudio")
            }
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={isUploading ? (uploadProgress ?? 0) : Math.round(progress)}
          >
            <div
              className={cn(
                "absolute top-0 left-0 h-full rounded-full",
                isUploading
                  ? "bg-utrecht-gold bg-gradient-to-r from-utrecht-gold via-utrecht-gold/70 to-utrecht-gold animate-pulse"
                  : "bg-primary"
              )}
              style={{
                width: `${isUploading ? (uploadProgress ?? 0) : progress}%`,
              }}
            />
            {!isUploading && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                style={{ left: `calc(${progress}% - 5px)` }}
              />
            )}
          </div>

          {/* Duration or upload progress */}
          <div className="flex items-center justify-between">
            {isUploading ? (
              <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                {uploadProgress ?? 0}%
              </span>
            ) : (
              <span className="text-xs text-muted-foreground whitespace-nowrap font-mono tabular-nums">
                {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : "--:--"}
              </span>
            )}

            {/* Transcript toggle */}
            {(transcriptText || (audioFileName && userId && conversationId)) && (
              <button
                type="button"
                onClick={handleTranscriptToggle}
                disabled={isLoadingTranscript}
                className={cn(
                  "flex items-center justify-end gap-1 text-xs transition-colors",
                  isLoadingTranscript
                    ? "text-muted-foreground/50 cursor-wait"
                    : "text-muted-foreground hover:text-foreground cursor-pointer"
                )}
                aria-label="Transcript"
              >
                <span className="hidden sm:inline">
                  {isLoadingTranscript
                    ? t("multimodal.loadingTranscript")
                    : transcriptError
                      ? transcriptError
                      : t("multimodal.clickToViewTranscript")}
                </span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-200",
                    isTranscriptExpanded ? "rotate-0" : "-rotate-90"
                  )}
                />
              </button>
            )}
          </div>
        </div>

        {removable && (
          <button
            type="button"
            className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive rounded-full p-1 transition-colors cursor-pointer flex-shrink-0"
            onClick={onRemove}
            aria-label={t("multimodal.removeAudio")}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div
        className={cn(
          "grid transition-all duration-300 ease-in-out",
          isTranscriptExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          {isLoadingTranscript ? (
            <div className="mt-2 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                Transcript laden...
              </p>
            </div>
          ) : transcriptError ? (
            <div className="mt-2 px-3 py-2 bg-destructive/10 rounded-md border-l-2 border-destructive/30">
              <p className="text-xs text-destructive">{transcriptError}</p>
            </div>
          ) : transcriptText ? (
            <div className="mt-2 px-3 py-2">
              <p className="text-xs text-muted-foreground font-mono leading-relaxed whitespace-pre-wrap">
                {transcriptText}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
